import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Field, FlagRule } from "@/components/common";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

function Hero() {
  const { t } = useLang();
  return (
    <div className="relative hidden overflow-hidden lg:block">
      <img src="/images/dubai.jpg" alt="Dubai skyline" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#04140B] via-[#04140B]/70 to-[#04140B]/20" />
      <div className="relative flex h-full flex-col justify-between p-12 text-white">
        <div className="flex items-center gap-3">
          <img src="/images/logo-compact-dark.png" alt="Mohassib" className="h-16 w-auto" />
        </div>
        <div className="max-w-lg rise">
          <h2 className="font-display text-5xl font-extrabold leading-[1.05] tracking-tight">{t("heroTitle")}</h2>
          <p className="mt-5 text-base leading-relaxed text-white/75">{t("heroText")}</p>
          <ul className="mt-8 space-y-3">
            {["heroPoint1", "heroPoint2", "heroPoint3"].map((k) => (
              <li key={k} className="flex items-center gap-3 text-sm"><CheckCircle2 className="h-4 w-4 text-[#00C462]" />{t(k)}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default function AuthPage({ mode }) {
  const { t, lang } = useLang();
  const { user, login, register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", name: "", company_name: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const isLogin = mode === "login";
  if (user) return <Navigate to="/dashboard" replace />;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (isLogin) await login(form.email, form.password);
      else await register({ ...form, lang });
      nav("/dashboard");
    } catch (err) {
      setError(errMsg(err));
    }
    setBusy(false);
  };

  return (
    <div className="grid min-h-screen bg-[#F6F7F4] lg:grid-cols-[1.1fr_1fr]">
      <Hero />
      <div className="flex flex-col">
        <FlagRule />
        <div className="flex justify-end p-6"><LanguageSwitcher /></div>
        <div className="flex flex-1 items-center justify-center px-6 pb-16">
          <form onSubmit={submit} data-testid={`${mode}-form`} className="rise w-full max-w-sm space-y-5">
            <div>
              <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-900">{t(isLogin ? "loginTitle" : "registerTitle")}</h1>
              <p className="mt-2 text-sm text-slate-500">{t(isLogin ? "loginSubtitle" : "registerSubtitle")}</p>
            </div>
            {!isLogin && (
              <>
                <Field label={t("fullName")}><Input data-testid="register-name-input" required value={form.name} onChange={set("name")} /></Field>
                <Field label={t("companyName")}><Input data-testid="register-company-input" value={form.company_name} onChange={set("company_name")} /></Field>
              </>
            )}
            <Field label={t("email")}><Input data-testid={`${mode}-email-input`} type="email" required value={form.email} onChange={set("email")} /></Field>
            <Field label={t("password")}><Input data-testid={`${mode}-password-input`} type="password" required minLength={6} value={form.password} onChange={set("password")} /></Field>
            {error && <div data-testid={`${mode}-error`} className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-[#A50E1F]">{error}</div>}
            <Button data-testid={`${mode}-submit-btn`} disabled={busy} className="h-11 w-full bg-[#007A3D] text-white hover:bg-[#005E2E]">
              {busy && <Loader2 className="me-2 h-4 w-4 animate-spin" />}{t(isLogin ? "login" : "register")}
            </Button>
            <p className="text-center text-sm text-slate-500">
              {t(isLogin ? "noAccount" : "haveAccount")}{" "}
              <Link data-testid={`goto-${isLogin ? "register" : "login"}-link`} to={isLogin ? "/register" : "/login"} className="font-semibold text-[#007A3D] hover:underline">
                {t(isLogin ? "register" : "login")}
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
